'use client'

import { Content, Portal } from '@radix-ui/react-hover-card'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { cn } from '#src/cn.ts'
import {
  canScrollFurther,
  findScrollableAncestor,
} from '#src/components/HoverCard/findScrollableAncestor.ts'

export interface HoverCardContentProps extends ComponentPropsWithoutRef<
  typeof Content
> {
  // Resolved from the hover-card-viewport-layout flag. This package cannot read
  // feature flags — the context lives in @seen/react — so the answer is passed
  // down, the same way leaf packages take a resolved snapshot. Consumers that
  // import this primitive directly stay on the pre-flag behavior until the flag
  // graduates and this prop goes away.
  viewportLayout?: boolean
  // The element the card is placed against, usually the list row. Supplying it
  // opts the card into forwarding a wheel gesture it cannot consume itself back
  // to the list this element scrolls within.
  scrollChainAnchor?: RefObject<HTMLElement | null>
}

// max-w/max-h clamp the card to the space Radix measured. Radix can only flip a
// left/right placement, never shift it sideways, so in a narrow window a card
// wider than both gaps overflows with no recourse from collision handling — a
// size cap is the only lever.
const BASE_CLASSES =
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-hover-card-content-available-height) w-64 max-w-(--radix-hover-card-content-available-width) origin-(--radix-hover-card-content-transform-origin) overflow-y-auto rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in'

// overflow-x-hidden is load-bearing, not redundant: per the CSS overflow spec a
// `visible` axis computes to `auto` when the other axis scrolls, so
// overflow-y-auto alone silently makes the card scroll sideways. wrap-break-word
// then keeps unbreakable runs — codes, URLs, emails — reflowing inside the cap
// rather than clipping against it. Descendants that set their own white-space
// still win.
const REFLOW_CLASSES = 'overflow-x-hidden wrap-break-word'

// Radix defaults collision padding to 0, which lets a card sit flush against the
// viewport edge. 8 read as cramped once a card clamps there, but 16 lands within
// a pixel of the facesheet card's own 15px inset, so a clamped hover card's
// border sat exactly on the widget's. 24 keeps it visibly inside that edge.
export const HOVER_CARD_EDGE_GAP = 24
const LEGACY_EDGE_GAP = 8

// The resting gap between a card and the element it points at. Exported so a
// caller computing its own offset can start from the same number.
export const HOVER_CARD_SIDE_OFFSET = 4

export const HoverCardContent = forwardRef<
  ElementRef<typeof Content>,
  HoverCardContentProps
>(
  (
    {
      className,
      align = 'center',
      sideOffset = HOVER_CARD_SIDE_OFFSET,
      collisionPadding,
      viewportLayout = false,
      scrollChainAnchor,
      ...props
    },
    ref
  ) => {
    // The wheel handler below has to be a native non-passive listener, which
    // needs the element itself. State rather than a ref so attaching it is an
    // effect dependency and survives the node being replaced.
    const [card, setCard] = useState<ElementRef<typeof Content> | null>(null)

    const composedRef = useCallback(
      (node: ElementRef<typeof Content> | null) => {
        setCard(node)
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    useEffect(() => {
      if (!card || !scrollChainAnchor) {
        return
      }

      function handleWheel(event: WheelEvent): void {
        // The card is portaled outside any react-remove-scroll container, so
        // that library's wheel handler cancels wheel events over it — the card
        // caps its own height and scrolls, but only the scrollbar worked.
        // Stopping propagation keeps the event from reaching that handler.
        // https://github.com/radix-ui/primitives/issues/1159
        event.stopPropagation()

        // While the card has room of its own the gesture is left entirely
        // native, so a capped card consumes deltas up to its limit and only the
        // remainder spills to the list on a later event.
        if (!card || canScrollFurther(card, event.deltaY)) {
          return
        }

        // The card floats over the list it describes and, in the default panel,
        // over the timeline as well. Without this the browser would still run
        // its own scroll underneath the forwarded one — the list moving twice
        // per notch, and the timeline moving at the same time.
        event.preventDefault()
        findScrollableAncestor(scrollChainAnchor?.current ?? null)?.scrollBy({
          top: event.deltaY,
        })
      }

      // Element-level listeners may opt out of passive; only React's root
      // delegation is passive, which is why this cannot be an onWheel prop.
      card.addEventListener('wheel', handleWheel, { passive: false })
      return () => {
        card.removeEventListener('wheel', handleWheel)
      }
    }, [card, scrollChainAnchor])

    return (
      <Portal>
        <Content
          ref={composedRef}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={
            collisionPadding ??
            (viewportLayout ? HOVER_CARD_EDGE_GAP : LEGACY_EDGE_GAP)
          }
          // Without an anchor the card only shields itself from the scroll-lock
          // layer; the listener above takes over both jobs when there is one.
          onWheel={
            viewportLayout && !scrollChainAnchor
              ? (e) => e.stopPropagation()
              : undefined
          }
          onTouchMove={viewportLayout ? (e) => e.stopPropagation() : undefined}
          className={cn(
            BASE_CLASSES,
            viewportLayout && REFLOW_CLASSES,
            className
          )}
          {...props}
        />
      </Portal>
    )
  }
)
HoverCardContent.displayName = 'HoverCardContent'
